<script lang="ts" setup>
import AlertMessage from "@/components/messages/AlertMessage.vue";
import DetachTruckDialog from "@/views/trips/edit/components/dialogs/DetachTruckDialog.vue";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import type { TripOrder, TripRoute, TripStop } from "@/types/trip";
import { computed, ref } from "vue";
import { RouteRedispatchState, RouteStatus } from "@/constants/trip";
import { PlannedRouteMessage } from "@/constants/route";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import DispatchDialog from "@/views/trips/edit/components/dialogs/DispatchDialog.vue";
import CancelledChip from "@/components/chips/CancelledChip.vue";
import PlannedChip from "@/components/chips/PlannedChip.vue";
import AttachTruckDialog from "@/views/trips/edit/components/dialogs/AttachTruckDialog.vue";
import TooltipButton from "@/components/buttons/TooltipButton.vue";
import type { DisabledRule } from "@/types/validation";
import { useDisableRules } from "@/composables/reactive/useDisableRules";
import { useFreights } from "@/composables";
import { OrderStopType } from "@/constants/order";
import VerticalDotsButton from "@/components/buttons/VerticalDotsButton.vue";
import { AlertMessageType } from "@/constants/components";
import { useI18n } from "vue-i18n";

const route = defineModel<TripRoute | null>({ default: null });

const { createTripDispatchDisableRules, createAddTruckToRouteDisableRules } = useDisableRules();
const { hasRouteFreightMismatch } = useFreights();
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    canRemove?: boolean;
    orders: TripOrder[];
    tripStops: TripStop[];
  }>(),
  {
    canRemove: false,
  },
);

const emits = defineEmits<{
  (e: "update:modelValue", value: TripRoute): void;
  (e: "attach", value: TruckDto): void;
  (e: "removeTruck"): void;
  (e: "removeRoute"): void;
}>();

const attachDialog = ref(false);
const showDispatchDialog = ref(false);
const removeTruckDialog = ref(false);
const removeRouteDialog = ref(false);

const orderFreights = computed(() => {
  return props.orders.flatMap((order) => order.freights);
});

const dispatchValidationRules = computed<DisabledRule[]>(() => {
  return route.value ? createTripDispatchDisableRules(route.value, false, orderFreights.value) : [];
});

const hasFreightMismatch = computed(() => {
  if (!route.value || route.value.status === RouteStatus.cancelled) {
    return false;
  }

  return hasRouteFreightMismatch(route.value.freights, orderFreights.value);
});

const attachTruckValidationRules = computed(() => {
  return route.value ? createAddTruckToRouteDisableRules(route.value) : [];
});

const needRedispatch = computed(() => {
  return route.value ? route.value.redispatch === RouteRedispatchState.needed && !route.value.isDispatched : false;
});

const isPlanned = computed(() => {
  return route.value?.status === RouteStatus.planned;
});

const isCancelled = computed(() => {
  return route.value?.status === RouteStatus.cancelled;
});

const tripBlockClass = computed(() => {
  if (isCancelled.value) {
    return "bg-red-lighten-5";
  }

  return "bg-white";
});

const truckNumberClass = computed(() => {
  if (isCancelled.value) {
    return "";
  }

  return "bg-grey-lighten-4";
});

const firstActivePickUp = computed(() => {
  return route.value?.routeStops.find((routeStop) => routeStop.type === OrderStopType.pickup && routeStop.use);
});

const firstActivePickUpFacilityCoordinates = computed(() => {
  return firstActivePickUp.value?.facility?.addressCoordinates || null;
});

function attach(value: TruckDto) {
  attachDialog.value = false;
  emits("attach", value);
}

function removeTruck(value: boolean) {
  removeTruckDialog.value = value;
  emits("removeTruck");
}
</script>

<template>
  <div v-if="route" :class="tripBlockClass" class="trip-block" data-qa="route-block">
    <div :class="{ 'border-warning': needRedispatch }" class="trip-block__inner pa-4 rounded-lg">
      <div class="trip-block__header d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <span class="text-grey-darken-2 font-weight-medium">Route</span>
          <planned-chip v-if="isPlanned" class="ml-3" />
          <cancelled-chip v-if="isCancelled" class="ml-3" />
        </div>

        <v-menu v-if="canRemove" offset-y>
          <template #activator="{ props: menuProps }">
            <vertical-dots-button v-bind="menuProps" />
          </template>
          <v-list>
            <v-tooltip :disabled="!route.id" location="bottom">
              <template #activator="{ props: tooltipProps }">
                <div v-bind="tooltipProps">
                  <v-list-item
                    :disabled="Boolean(route.id)"
                    class="cursor-pointer"
                    v-bind="tooltipProps"
                    @click="removeRouteDialog = true"
                  >
                    <v-list-item-title>Remove route</v-list-item-title>
                  </v-list-item>
                </div>
              </template>

              <span>This route is already saved in the system and can`t be removed</span>
            </v-tooltip>
          </v-list>
        </v-menu>
      </div>
      <div
        v-if="route?.truck"
        :class="truckNumberClass"
        class="d-flex align-center border-grey-lighter-1 justify-space-between pa-4 rounded"
      >
        <span class="font-weight-medium text-primary" data-qa="truck-number">Truck #{{ route.truck.number }}</span>
      </div>

      <tooltip-button v-if="route?.truck" :rules="dispatchValidationRules">
        <template #default="{ disabled }">
          <v-btn
            :disabled="disabled"
            :variant="route.isDispatched ? 'outlined' : 'flat'"
            block
            class="btn text-uppercase mt-3"
            color="primary"
            @click="showDispatchDialog = true"
          >
            <template v-if="!route.isDispatched">Dispatch</template>
            <template v-else-if="isCancelled">View settings</template>
            <template v-else>Dispatch settings</template>
          </v-btn>
        </template>
      </tooltip-button>

      <tooltip-button v-else :rules="attachTruckValidationRules">
        <template #default="{ disabled }">
          <v-btn
            :disabled="disabled"
            block
            class="text-uppercase font-weight-600"
            color="primary"
            variant="outlined"
            @click="attachDialog = true"
          >
            Attach truck/Carrier
          </v-btn>
        </template>
      </tooltip-button>
      <alert-message v-if="hasFreightMismatch" :type="AlertMessageType.danger" class="mt-3">
        {{ t("trip.synchronize-freight-from-order") }}
      </alert-message>
      <alert-message
        v-if="route.technicalPartial"
        bg-color="indigo-lighten-5"
        border-color="none"
        class="mt-3"
        icon="mdi-information-outline"
        icon-color="indigo"
      >
        <span class="text-indigo font-weight-500"> This route will be processed as Technical Partial </span>
      </alert-message>
      <alert-message v-if="needRedispatch" class="mt-3"> Please update information for driver</alert-message>
      <alert-message v-if="isPlanned" bg-color="indigo-lighten-5" border-color="none" class="mt-3" icon-color="indigo">
        <span class="text-indigo font-weight-500">
          {{ PlannedRouteMessage }}
        </span>
      </alert-message>
      <alert-message v-if="route.wrongEmptyMiles" :type="AlertMessageType.warning" class="mt-3"
        >Previous planned route for this truck has been changed. Empty miles will be automatically recalculated after
        saving trip</alert-message
      >
    </div>

    <omni-dialog v-model="attachDialog" title="Attach truck" width="850">
      <attach-truck-dialog :coordinates="firstActivePickUpFacilityCoordinates" @attach="attach" />
    </omni-dialog>

    <omni-dialog
      v-model="removeTruckDialog"
      :title="'Remove truck #' + route?.truck?.number"
      primary-action-label="Remove"
      width="360"
      @primary-action:click="removeTruck"
    >
      <detach-truck-dialog v-if="route?.truck" :truck="route.truck" />
    </omni-dialog>

    <dispatch-dialog v-model="showDispatchDialog" v-model:route="route" :orders="orders" :trip-stops="tripStops" />

    <omni-dialog
      v-model="removeRouteDialog"
      primary-action-label="Remove"
      title="Remove route"
      width="360"
      @primary-action:click="$emit('removeRoute')"
    >
      <div class="text-grey-darken-1 py-4">Are you sure want to remove from trip?</div>
    </omni-dialog>
  </div>
</template>

<style scoped>
.trip-block__inner {
  min-height: 180px;
}
</style>
